import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AffiliateType } from '../../../../../models/enums/affiliate_type.enum';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { AlertService } from '../../../../../core/services/alert/alert.service';
import { ApiService } from '../../../../../core/services/api/api.service';
import { ToastService } from '../../../../../core/services/toast/toast.service';
import { cloneDeep } from 'lodash-es';
import { Affiliate } from '../../../../../models/interfaces/affiliate.interface';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';

@Component({
    selector: 'app-modal-edit-affiliate',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatOptionModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatButtonModule
    ],
    templateUrl: './modal-edit-affiliate.component.html',
    styleUrl: './modal-edit-affiliate.component.scss'
})
export class ModalEditAffiliateComponent implements OnInit, OnDestroy {
  editMode = false;
  affiliateForm: FormGroup = new FormGroup({});
  showCloseAlert = false;
  isSavingAffiliate = false;
  isNewUser = false;
  isVerifyingEmail = false;
  emailVerified = false;
  affiliateTypes = [
    {
      label: 'Individuo no fiscal',
      value: AffiliateType.Single
    },
    {
      label: 'Autónomo',
      value: AffiliateType.Freelance
    },
    {
      label: 'Empresa',
      value: AffiliateType.Company
    }
  ];

  private unsubscribe$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ModalEditAffiliateComponent>,
    private alert: AlertService,
    private api: ApiService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  initForm(): void {
    const receivedData = this.data ? cloneDeep(this.data) : null;
    const receivedAffiliate: Affiliate = receivedData?.affiliate;
    if (receivedAffiliate) {
      this.editMode = true;
      this.emailVerified = true;
    }

    this.affiliateForm = new FormGroup({
      userId: new FormControl(receivedAffiliate?.userId || null),
      email: new FormControl(receivedAffiliate?.email || null, [Validators.required, Validators.email]),
      name: new FormControl(receivedAffiliate?.name || null, Validators.required),
      lastname: new FormControl(receivedAffiliate?.lastname || null, Validators.required),
      affiliateType: new FormControl(receivedAffiliate?.affiliateType || null, Validators.required),
      affiliateDni: new FormControl(receivedAffiliate?.affiliateDni || null),
      affiliateCif: new FormControl(receivedAffiliate?.affiliateCif || null),
      affiliateCompany: new FormControl(receivedAffiliate?.affiliateCompany || null),
      affiliateAddress: new FormControl(receivedAffiliate?.affiliateAddress || null),
      affiliatePostalCode: new FormControl(receivedAffiliate?.affiliatePostalCode || null),
      affiliateUsername: new FormControl(receivedAffiliate?.affiliateUsername || null, [Validators.required, Validators.pattern(/^[a-zA-Z0-9-]*$/)]),
      affiliateFeePercentage: new FormControl(receivedAffiliate?.affiliateFeePercentage || null, [
        Validators.required,
        Validators.min(0),
        Validators.max(100)
      ])
    });
    this.listenForChanges();
  }

  listenForChanges(): void {
    this.affiliateForm.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
      this.showCloseAlert = true;
      const affiliateType = value.affiliateType;
      if (affiliateType === AffiliateType.Single) {
        this.affiliateForm.controls.affiliateDni.clearValidators();
        this.affiliateForm.controls.affiliateCif.clearValidators();
        this.affiliateForm.controls.affiliateCompany.clearValidators();
        this.affiliateForm.controls.affiliateAddress.clearValidators();
        this.affiliateForm.controls.affiliatePostalCode.clearValidators();

        this.affiliateForm.controls.affiliateDni.updateValueAndValidity({ emitEvent: false });
        this.affiliateForm.controls.affiliateCif.updateValueAndValidity({ emitEvent: false });
        this.affiliateForm.controls.affiliateCompany.updateValueAndValidity({ emitEvent: false });
        this.affiliateForm.controls.affiliateAddress.updateValueAndValidity({ emitEvent: false });
        this.affiliateForm.controls.affiliatePostalCode.updateValueAndValidity({ emitEvent: false });
      } else if (affiliateType === AffiliateType.Freelance) {
        this.affiliateForm.controls.affiliateDni.setValidators(Validators.required);
        this.affiliateForm.controls.affiliateCif.clearValidators();
        this.affiliateForm.controls.affiliateCompany.clearValidators();

        this.affiliateForm.controls.affiliateDni.updateValueAndValidity({ emitEvent: false });
        this.affiliateForm.controls.affiliateCif.updateValueAndValidity({ emitEvent: false });
        this.affiliateForm.controls.affiliateCompany.updateValueAndValidity({ emitEvent: false });
      } else if (affiliateType === AffiliateType.Company) {
        this.affiliateForm.controls.affiliateDni.clearValidators();
        this.affiliateForm.controls.affiliateCif.setValidators(Validators.required);
        this.affiliateForm.controls.affiliateCompany.setValidators(Validators.required);

        this.affiliateForm.controls.affiliateDni.updateValueAndValidity({ emitEvent: false });
        this.affiliateForm.controls.affiliateCif.updateValueAndValidity({ emitEvent: false });
        this.affiliateForm.controls.affiliateCompany.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  async confirmCloseModal(): Promise<any> {
    if (this.showCloseAlert) {
      const closeModal = await this.alert.presentCloseWindowAlert();
      return closeModal ? this.closeModal() : null;
    }

    this.closeModal();
  }

  closeModal(result?: any): void {
    this.dialogRef.close(result);
  }

  async verifyAffiliateEmail(): Promise<void> {
    try {
      this.isVerifyingEmail = true;
      const body = {
        email: this.affiliateForm.controls.email.value.trim().toLowerCase()
      };
      const response = await firstValueFrom(this.api.verifyAffiliateEmail(body));
      this.isVerifyingEmail = false;
      this.isNewUser = response.isNewUser;
      this.emailVerified = true;
      this.affiliateForm.controls.name.setValue(response.name);
      this.affiliateForm.controls.lastname.setValue(response.lastname);
      if (!this.isNewUser) {
        this.affiliateForm.controls.name.disable();
        this.affiliateForm.controls.lastname.disable();
      } else {
        this.affiliateForm.controls.name.enable();
        this.affiliateForm.controls.lastname.enable();
      }
    } catch (errorResponse: any) {
      this.isVerifyingEmail = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error verificando email');
    }
  }

  async saveAffiliate(): Promise<void> {
    try {
      this.isSavingAffiliate = true;
      const body = {
        ...this.affiliateForm.value
      };
      body.name = this.affiliateForm.controls.name.value;
      body.lastname = this.affiliateForm.controls.lastname.value;
      body.email = body.email.trim().toLowerCase();
      body.affiliateUsername = body.affiliateUsername.trim();

      let response = null;
      if (!this.editMode) {
        response = await firstValueFrom(this.api.createAffiliate(body));
      } else {
        response = await firstValueFrom(this.api.updateAffiliate(body));
      }
      this.isSavingAffiliate = false;
      this.closeModal({ affiliateUpdated: response.affiliateUpdated });
      this.toast.presentSuccessToast(response.message);
    } catch (errorResponse: any) {
      this.isSavingAffiliate = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error guardando afiliado');
    }
  }
}
